import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router';
import { Container, Box, Button, Alert, Backdrop } from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useOidc } from '@nohara-dxdev/react-oidc';

import { RepresentativesSection } from '../../components/representatives-section';
import { CompanyInfoSection } from '../../components/customer/info-section';
import { CurrentAddressSection } from '../../components/current-address-section';
import { HeadOfficeAddressSection } from '../../components/head-office-address-section';
import { CreatedUpdatedInfoSection } from '../../components/created-updated-info-section';
import { mappingEditCustomerData } from '../../utils/mappers/customer-form-mapper';
import {
  VALIDATION_MESSAGES,
  CUSTOMER_MESSAGES,
} from '../../utils/validations/validation-messages';
import { validateCustomerDetailForm } from '../../utils/validations';
import {
  CustomerDetailResponse,
  CustomerDetailForm,
  NoharaRepresentative,
  Address,
} from '../../types/company.types';
import { companiesService } from '../../services/companies.service';
import { useDepartments, useStaffs } from '../../hooks';
import { getStaffsFromDepartment } from '../../utils/common-staff-utils';
import { useMarkInitialDetailFieldsTouched } from '../../hooks/common/use-mark-initial-detail-fields-touched';
import { DepartmentContact } from '../../types/table/company.types';

import { useBeforeUnloadWarning } from '@/hooks/use-beforeunload-warning';
import { useSubmitting } from '@/hooks/use-submitting';
import { paths } from '@/config/paths';
import HeaderRegister from '@/features/apps/components/header-register';
import { checkPermission, getUniqueDepartmentContactLists, padPostalCode } from '@/utils/helpers';
import { FixedFooter } from '@/components/footer/fixed-footer';
import { MESSAGES } from '@/lib/constants';
import { useConfirmDialog } from '@/hooks/use-confirm-dialog';
import { useLoadingProgress } from '@/hooks/use-loading-progress';
import { useSnackbar } from '@/hooks/use-snackbar';
import { FunctionName, PermissionAction } from '@/lib/enums';

// Factory to create an empty CustomerDetailForm with defaults
const createEmptyCustomerDetailForm = (): CustomerDetailForm => ({
  galiAccountCode: '',
  formalName: '',
  abbreviationName: '',
  branchDepartment: '',
  partnerNameKana: '',
  customerCategory: undefined,
  currentAddress: {
    postalCode1: '',
    postalCode2: '',
    address1: '',
    address2: '',
    address3: '',
    phone: '',
    fax: '',
  },
  headOfficeAddress: {
    postalCode1: '',
    postalCode2: '',
    address1: '',
    address2: '',
    address3: '',
  },
  representatives: [
    {
      department: '',
      salesPerson: {
        id: '',
        firstName: '',
        lastName: '',
        code: '',
      },
    },
  ] as NoharaRepresentative[],
  isCommonCement: false,
});

export const CustomerDetailPage = () => {
  const { customerId } = useParams<{ customerId: string }>();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [loadingError, setLoadingError] = useState<string | null>(null);
  const { setConfirmDialog } = useConfirmDialog();
  const { isLoading, setIsLoading } = useLoadingProgress();
  const { setSnackbarMessage } = useSnackbar();
  const { setIsSubmitting } = useSubmitting();
  const { permissions } = useOidc();
  // Form state using useState
  const [formData, setFormData] = useState<CustomerDetailForm>(createEmptyCustomerDetailForm());

  const [originalFormData, setOriginalFormData] = useState<CustomerDetailForm>({
    ...createEmptyCustomerDetailForm(),
    createdAt: undefined,
    createdUser: undefined,
    updatedAt: undefined,
    updatedUser: undefined,
  });

  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());
  const hasFetchedData = useRef(false);

  // Global state hooks
  const { departments, loading: departmentsLoading } = useDepartments(PermissionAction.UPDATE);
  const { staffsByDepartment, loading: staffsLoading, loadStaffsByDepartment } = useStaffs();

  // Validate form whenever formData changes
  const validation = useMemo(() => {
    const result = validateCustomerDetailForm(formData);
    return result;
  }, [formData, touchedFields]);

  // Mark required fields as touched & log initial errors after data is loaded
  useMarkInitialDetailFieldsTouched({
    formData,
    touchedFields,
    setTouchedFields,
    hasFetchedData,
    validateForm: validateCustomerDetailForm,
  });

  // Form field update function
  const updateField = (field: keyof CustomerDetailForm, value: unknown) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setTouchedFields((prev) => new Set(prev).add(String(field)));
  };

  // Representative management
  const addRepresentative = () => {
    if (formData.representatives.length < 10) {
      const newRepresentative: NoharaRepresentative = {
        department: '',
        departmentCode: '',
        salesPerson: {
          id: '',
          firstName: '',
          lastName: '',
          code: '',
        },
      };
      const newIndex = formData.representatives.length;

      setFormData((prev) => {
        const updated = {
          ...prev,
          representatives: [...prev.representatives, newRepresentative],
        };
        return updated;
      });

      // Mark new representative fields as touched to trigger validation immediately
      setTouchedFields((prev) => {
        const newTouched = new Set(prev);
        const deptField = `representatives.${newIndex}.department`;
        const salesField = `representatives.${newIndex}.salesPerson`;
        newTouched.add(deptField);
        newTouched.add(salesField);
        return newTouched;
      });
    }
  };

  const updateRepresentative = (
    id: string,
    field: keyof NoharaRepresentative,
    value: NoharaRepresentative[keyof NoharaRepresentative]
  ) => {
    const index = Number.parseInt(id, 10);
    setFormData((prev) => ({
      ...prev,
      representatives: prev.representatives.map((rep, i) => {
        if (i === index) {
          // If department changes, reset salesPerson
          if (field === 'department') {
            return {
              ...rep,
              [field]: value,
              salesPerson: {
                id: '',
                firstName: '',
                lastName: '',
                code: '',
              },
            } as NoharaRepresentative;
          }
          return { ...rep, [field]: value } as NoharaRepresentative;
        }
        return rep;
      }),
    }));
    setTouchedFields((prev) => new Set(prev).add(`representatives.${index}.${field}`));
  };

  // Helper to process touched field after representative removal
  const processFieldAfterRemoval = (field: string, removedIndex: number): string | null => {
    // Skip fields for removed representative
    if (
      field === `representatives.${removedIndex}.department` ||
      field === `representatives.${removedIndex}.salesPerson`
    ) {
      return null;
    }

    // Re-index fields after removed representative
    const match = /^representatives\.(\d+)\.(.+)$/.exec(field);
    if (!match) return field;

    const fieldIndex = Number.parseInt(match[1]);
    const fieldName = match[2];

    if (fieldIndex > removedIndex) {
      // Decrement index for fields after removed one
      return `representatives.${fieldIndex - 1}.${fieldName}`;
    }

    return field;
  };

  const removeRepresentative = (id: string) => {
    const index = Number.parseInt(id, 10);
    setFormData((prev) => ({
      ...prev,
      representatives: prev.representatives.filter((_, i) => i !== index),
    }));

    // Clean up touched fields for removed representative and re-index remaining ones
    setTouchedFields((prev) => {
      const newTouched = new Set<string>();
      for (const field of prev) {
        const processedField = processFieldAfterRemoval(field, index);
        if (processedField !== null) {
          newTouched.add(processedField);
        }
      }
      return newTouched;
    });
  };

  const getSalesPersonOptions = (departmentId: string) => {
    return getStaffsFromDepartment(staffsByDepartment, departmentId);
  };

  const canAddRepresentative = formData.representatives.length < 10;

  // Check if form has changes (customer-specific validation)
  const hasChanges = useMemo(() => {
    // Check basic info changes
    const basicInfoChanged =
      formData.formalName !== originalFormData.formalName ||
      formData.abbreviationName !== originalFormData.abbreviationName ||
      formData.branchDepartment !== originalFormData.branchDepartment ||
      formData.partnerNameKana !== originalFormData.partnerNameKana ||
      formData.customerCategory !== originalFormData.customerCategory ||
      formData.galiAccountCode !== originalFormData.galiAccountCode;

    // Check address changes
    const currentAddressChanged =
      JSON.stringify(formData.currentAddress) !== JSON.stringify(originalFormData.currentAddress);
    const headOfficeAddressChanged =
      JSON.stringify(formData.headOfficeAddress) !==
      JSON.stringify(originalFormData.headOfficeAddress);

    // Check representatives changes
    const representativesChanged =
      JSON.stringify(formData.representatives) !== JSON.stringify(originalFormData.representatives);

    return (
      basicInfoChanged ||
      currentAddressChanged ||
      headOfficeAddressChanged ||
      representativesChanged
    );
  }, [formData, originalFormData]);

  // Show popup warning when user tries to close/refresh page with unsaved changes
  useBeforeUnloadWarning(hasChanges);

  const handleDepartmentChange = (departmentId: string) => {
    void loadStaffsByDepartment('sale', departmentId, FunctionName.COMPANY_MANAGEMENT);
  };

  // Address management
  const updateCurrentAddress = (field: keyof Address, value: string) => {
    setFormData((prev) => ({
      ...prev,
      currentAddress: { ...prev.currentAddress, [field]: value },
    }));
    setTouchedFields((prev) => new Set(prev).add(`currentAddress.${field}`));
  };

  const updateHeadOfficeAddress = (field: keyof Address, value: string) => {
    setFormData((prev) => ({
      ...prev,
      headOfficeAddress: { ...prev.headOfficeAddress, [field]: value },
    }));
    setTouchedFields((prev) => new Set(prev).add(`headOfficeAddress.${field}`));
  };

  // Individual field validation functions using validation function results
  const getFieldError = useCallback(
    (fieldName: string): string | undefined => {
      // Map component field name to validation field name
      const validationFieldName = mapFieldNameForValidation(fieldName);

      // Only show errors for fields that have been touched
      const isTouched = touchedFields.has(validationFieldName);
      if (!isTouched) {
        return undefined;
      }

      // Get error from validation results
      const error = validation.errors.find((e) => e.field === validationFieldName);
      const errorMessage = error?.message;

      return errorMessage;
    },
    [validation.errors, touchedFields, formData.representatives.length]
  );

  const isFieldValid = useCallback(
    (fieldName: string): boolean => {
      // Map component field name to validation field name
      const validationFieldName = mapFieldNameForValidation(fieldName);

      // Only show errors for fields that have been touched
      if (!touchedFields.has(validationFieldName)) return true;

      const hasError = validation.errors.some((e) => e.field === validationFieldName);
      return !hasError;
    },
    [validation.errors, touchedFields]
  );

  // Helper function to preload staffs for departments
  const preloadStaffsForDepartments = useCallback(
    async (departmentContactLists: DepartmentContact[]) => {
      if (!departmentContactLists || departmentContactLists.length === 0) {
        return;
      }

      const uniqueDepartments = getUniqueDepartmentContactLists(departmentContactLists);

      const loadStaffsPromises = uniqueDepartments.map(async (contact: DepartmentContact) => {
        if (!contact.departmentId || !contact.canUpdate) return;

        try {
          await loadStaffsByDepartment(
            'sale',
            contact.departmentId,
            FunctionName.COMPANY_MANAGEMENT
          );
        } catch {
          // Ignore preloading errors
        }
      });

      await Promise.all(loadStaffsPromises);
      await new Promise((resolve) => setTimeout(resolve, 100));
    },
    [departments, loadStaffsByDepartment]
  );

  // Helper to map a single representative from API response
  const mapRepresentativeFromResponse = useCallback(
    (contact: DepartmentContact, index: number): NoharaRepresentative => {
      const departmentId = contact.departmentId;

      // If department is missing or not found, clear salesPerson to make it an empty representative
      // This ensures validation will skip it (empty representatives are skipped)
      const hasValidDepartment = !!departmentId;

      const staff =
        hasValidDepartment && contact.saleStaff
          ? {
              id: contact.saleStaff.id,
              firstName: contact.saleStaff.lastName,
              lastName: contact.saleStaff.firstName,
              code: contact.contactPersonCode,
            }
          : {
              id: '',
              firstName: '',
              lastName: '',
              code: '',
            };

      return {
        department: departmentId,
        departmentCode: contact.departmentCode,
        salesPerson: staff,
        canUpdate: contact.canUpdate,
        canView: contact.canView,
        departmentName: contact.departmentName,
        originalIndex: index,
      } as unknown as NoharaRepresentative;
    },
    []
  );

  // Data fetching
  useEffect(() => {
    const fetchCustomerData = async () => {
      setIsLoading(true);
      if (!customerId || hasFetchedData.current) return;

      try {
        setLoadingError(null);

        const response = await companiesService.getCustomerById(customerId);

        const customerData = (response as { data: { data: CustomerDetailResponse } }).data.data;

        // Preload staffs for all departments in departmentContactLists
        const departmentContactLists = (customerData.departmentContactLists ??
          []) as DepartmentContact[];
        await preloadStaffsForDepartments(departmentContactLists);

        // Map representatives from API response
        const representatives = departmentContactLists.map((contact, index) =>
          mapRepresentativeFromResponse(contact, index)
        );
        // Ensure at least one representative exists
        const finalRepresentatives =
          representatives.length === 0
            ? [{ department: '', salesPerson: { id: '', firstName: '', lastName: '', code: '' } }]
            : representatives;

        // Map API response to form data
        const mappedFormData: CustomerDetailForm = {
          galiAccountCode: customerData.galiAccountCode?.toString() || '',
          formalName: customerData.formalName || '',
          abbreviationName: customerData.abbreviationName || '',
          branchDepartment: customerData.branchDepartment || '',
          partnerNameKana: customerData.partnerNameKana || '',
          customerCategory: customerData.customerCategory,
          currentAddress: {
            postalCode1: padPostalCode(customerData.locationZipCodeFirst, 3),
            postalCode2: padPostalCode(customerData.locationZipCodeLast, 4),
            address1: customerData.locationAddress1 || '',
            address2: customerData.locationAddress2 || '',
            address3: customerData.locationAddress3 || '',
            phone: customerData.locationPhone || '',
            fax: customerData.locationFax || '',
          },
          headOfficeAddress: {
            postalCode1: padPostalCode(customerData.headquartersAddressZipCodeFirst, 3),
            postalCode2: padPostalCode(customerData.headquartersAddressZipCodeLast, 4),
            address1: customerData.headquartersAddress1 || '',
            address2: customerData.headquartersAddress2 || '',
            address3: customerData.headquartersAddress3 || '',
          },
          canUpdate: customerData.canUpdate,
          representatives: finalRepresentatives,
          isCommonCement: false, // Default value
          createdAt: customerData.createdAt,
          createdUser: customerData.createdUser,
          updatedAt: customerData.updatedAt,
          updatedUser: customerData.updatedUser,
          createdStaff: customerData.createdStaff,
          updatedStaff: customerData.updatedStaff,
        };

        setFormData(mappedFormData);
        setOriginalFormData(mappedFormData);
        hasFetchedData.current = true;
      } catch (err) {
        console.error('Error fetching customer data:', err);
        setError('得意先データの取得に失敗しました。');
      } finally {
        setIsLoading(false);
      }
    };

    void fetchCustomerData();
  }, [customerId, preloadStaffsForDepartments, mapRepresentativeFromResponse, setIsLoading]);

  // Helper to check if a representative is valid for API
  const isRepresentativeValidForAPI = useCallback(
    (rep: NoharaRepresentative): boolean => {
      if (!rep.department || !rep.salesPerson) return false;

      const department = departments.find((dept) => dept.id === rep.department);
      const staff = {
        id: rep.salesPerson.id,
        firstName: rep.salesPerson.firstName,
        lastName: rep.salesPerson.lastName,
        code: Number(rep.salesPerson.code),
      };

      return !!(department?.code && staff?.code && department.code > 0 && staff.code > 0);
    },
    [departments, staffsByDepartment]
  );

  // Helper to validate representatives
  const validateRepresentatives = useCallback((): string[] => {
    const errors: string[] = [];

    if (formData.representatives.length === 0) {
      errors.push(VALIDATION_MESSAGES.REPRESENTATIVES_MIN_REQUIRED);
      return errors;
    }

    let hasValidRepresentative = false;

    for (const [index, rep] of formData.representatives.entries()) {
      // Check if this representative is empty (both department and salesPerson are empty)
      // Skip validation for empty representatives
      const hasDepartment = !!rep.department?.trim();
      const hasSalesPerson = !!rep.salesPerson?.id?.trim();
      const isEmptyRepresentative = !hasDepartment && !hasSalesPerson;

      // Only validate if the representative has at least one field filled
      if (!isEmptyRepresentative) {
        if (!hasDepartment) {
          errors.push(
            `野原担当 #${index + 1}: ${VALIDATION_MESSAGES.REPRESENTATIVE_DEPARTMENT_REQUIRED}`
          );
        }
        if (!hasSalesPerson) {
          errors.push(
            `野原担当 #${index + 1}: ${VALIDATION_MESSAGES.REPRESENTATIVE_SALES_PERSON_REQUIRED}`
          );
        }

        // Check if this representative is valid for API
        if (isRepresentativeValidForAPI(rep)) {
          hasValidRepresentative = true;
        }
      }
    }

    if (!hasValidRepresentative) {
      errors.push('少なくとも1つの有効な野原担当者を選択してください。');
    }

    return errors;
  }, [formData.representatives, isRepresentativeValidForAPI]);

  // Comprehensive validation function
  const validateForm = useCallback(() => {
    const errors: string[] = [];

    // Required fields validation
    if (!formData.formalName?.trim()) {
      errors.push(VALIDATION_MESSAGES.OFFICIAL_NAME_REQUIRED);
    }

    if (!formData.abbreviationName?.trim()) {
      errors.push(VALIDATION_MESSAGES.ABBREVIATED_NAME_REQUIRED);
    }

    if (!formData.customerCategory) {
      errors.push(CUSTOMER_MESSAGES.CUSTOMER_CATEGORY_DETAIL_REQUIRED);
    }

    // Address validation
    if (!formData.currentAddress.postalCode1?.trim()) {
      errors.push(VALIDATION_MESSAGES.CURRENT_ADDRESS_POSTAL_CODE1_REQUIRED);
    }

    if (!formData.currentAddress.postalCode2?.trim()) {
      errors.push(VALIDATION_MESSAGES.CURRENT_ADDRESS_POSTAL_CODE2_REQUIRED);
    }

    if (!formData.currentAddress.address1?.trim()) {
      errors.push(VALIDATION_MESSAGES.CURRENT_ADDRESS_ADDRESS1_REQUIRED);
    }

    // Head office address validation
    if (!formData.headOfficeAddress.postalCode1?.trim()) {
      errors.push(VALIDATION_MESSAGES.HEAD_OFFICE_POSTAL_CODE1_REQUIRED);
    }

    if (!formData.headOfficeAddress.postalCode2?.trim()) {
      errors.push(VALIDATION_MESSAGES.HEAD_OFFICE_POSTAL_CODE2_REQUIRED);
    }

    if (!formData.headOfficeAddress.address1?.trim()) {
      errors.push(VALIDATION_MESSAGES.HEAD_OFFICE_ADDRESS1_REQUIRED);
    }

    if (!formData.headOfficeAddress.address2?.trim()) {
      errors.push(VALIDATION_MESSAGES.HEAD_OFFICE_ADDRESS2_REQUIRED);
    }

    // Representatives validation
    const representativeErrors = validateRepresentatives();
    errors.push(...representativeErrors);

    return errors;
  }, [formData, validateRepresentatives]);

  // Helper to get all field names for validation
  const getAllFieldNamesForValidation = (): Set<string> => {
    const allFieldNames = new Set([
      'formalName',
      'abbreviationName',
      'customerCategory',
      'currentAddress.postalCode1',
      'currentAddress.postalCode2',
      'currentAddress.address1',
      'headOfficeAddress.postalCode1',
      'headOfficeAddress.postalCode2',
      'headOfficeAddress.address1',
      'headOfficeAddress.address2',
    ]);

    // Add representative fields
    for (const [index] of formData.representatives.entries()) {
      allFieldNames.add(`representatives.${index}.department`);
      allFieldNames.add(`representatives.${index}.salesPerson`);
    }

    return allFieldNames;
  };

  // Helper to handle validation errors
  const handleValidationErrors = (validationErrors: string[]) => {
    setTouchedFields(getAllFieldNamesForValidation());
    setError(validationErrors.join('\n'));

    // Scroll to first error field
    const firstErrorElement = document.querySelector('[data-error="true"]');
    if (firstErrorElement) {
      firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!customerId) return;

    setError(null);

    // Run validation
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      handleValidationErrors(validationErrors);
      return;
    }

    try {
      setIsLoading(true);

      // Map form data to API request format
      const requestData = mappingEditCustomerData(formData, departments);

      await companiesService.updateCustomerById(customerId, requestData);

      // Update original form data to reflect saved state
      setOriginalFormData(formData);
      setTouchedFields(new Set());
      setSnackbarMessage(MESSAGES.CUSTOMER_HAS_BEEN_CHANGED);
      // Redirect to companies page
      setIsSubmitting(true);
      navigate('/companies');
    } catch (err) {
      console.error('Error updating customer:', err);
      setError('得意先の更新に失敗しました。');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToList = () => {
    navigate(paths.companies.path);
  };

  const handleCancel = () => {
    navigate(-1);
  };

  // Helper to map registration form fields to detail form fields
  const handleInfoFieldUpdate = (field: string, value: unknown) => {
    const fieldMapping: Record<string, keyof CustomerDetailForm> = {
      galiCustomerCode: 'galiAccountCode',
      officialName: 'formalName',
      abbreviatedName: 'abbreviationName',
      branchDepartment: 'branchDepartment',
      customerNameKatakana: 'partnerNameKana',
      customerCategory: 'customerCategory',
    };

    const mappedField = fieldMapping[field];
    if (mappedField) {
      updateField(mappedField, value);
      // Also mark the validation field name as touched for proper error display
      setTouchedFields((prev) => new Set(prev).add(mappedField));
    }
  };

  // Helper to map form field names to validation field names for error display
  const mapFieldNameForValidation = (componentFieldName: string): string => {
    const validationFieldMapping: Record<string, string> = {
      galiCustomerCode: 'galiAccountCode',
      officialName: 'formalName',
      abbreviatedName: 'abbreviationName',
      branchDepartment: 'branchDepartment',
      customerNameKatakana: 'partnerNameKana',
      customerCategory: 'customerCategory',
    };

    return validationFieldMapping[componentFieldName] || componentFieldName;
  };

  // Helper to copy current address to head office address (excluding phone and fax)
  const handleCopyCurrentToHeadOffice = () => {
    setFormData((prev) => {
      const { postalCode1, postalCode2, address1, address2, address3 } = prev.currentAddress;
      return {
        ...prev,
        headOfficeAddress: { postalCode1, postalCode2, address1, address2, address3 },
      };
    });

    // Mark all head office address fields as touched to show validation immediately
    const headOfficeFields = ['postalCode1', 'postalCode2', 'address1', 'address2', 'address3'];

    setTouchedFields((prev) => {
      const next = new Set(prev);
      for (const field of headOfficeFields) {
        next.add(`headOfficeAddress.${field}`);
      }
      return next;
    });
  };

  const deleteCustomer = async (customerId: string) => {
    setIsLoading(true);
    try {
      await companiesService.deleteCustomer(customerId);
      // Navigate back to company list after successful deletion
      setSnackbarMessage(MESSAGES.DELETE_CUSTOMER);
      setIsSubmitting(true);
      navigate(paths.companies.path);
    } catch (error) {
      console.error('Error deleting customer:', error);
      const message = '削除に失敗しました。もう一度お試しください。';
      setConfirmDialog({
        title: message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteCustomer = async () => {
    if (!customerId) return;
    setConfirmDialog({
      title: '得意先情報を削除します。',
      confirmDialog: () => {
        void deleteCustomer(customerId);
      },
      confirmButtonText: '削除',
      cancelButtonText: 'キャンセル',
    });
  };

  // Check permission on mount - redirect to 404 if user doesn't have READ or UPDATE permission
  useEffect(() => {
    const canRead = checkPermission(
      permissions,
      FunctionName.COMPANY_MANAGEMENT,
      PermissionAction.READ
    );
    const canUpdate = checkPermission(
      permissions,
      FunctionName.COMPANY_MANAGEMENT,
      PermissionAction.UPDATE
    );

    if (!canRead && !canUpdate) {
      navigate('/404');
      return;
    }
  }, [permissions, navigate]);

  // Show loading error if data fetch failed
  if (loadingError) {
    return (
      <Container maxWidth='lg' sx={{ py: 4 }}>
        <Alert severity='error' sx={{ mb: 3 }}>
          {loadingError}
        </Alert>
        <Button variant='outlined' startIcon={<ArrowBackIcon />} onClick={handleBackToList}>
          会社一覧に戻る
        </Button>
      </Container>
    );
  }
  return (
    <>
      <Container maxWidth='lg' sx={{ py: 4 }}>
        {/* Header */}
        <HeaderRegister title='得意先詳細' description='得意先情報の詳細表示・編集' />

        {/* General Error Messages */}
        {error && (
          <Alert severity='error' sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ position: 'relative' }} inert={!formData.canUpdate ? true : false}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Customer Info Section */}
            <CompanyInfoSection
              // canUpdate={canUpdateCustomer}
              formData={{
                companyType: '得意先' as const,
                galiCustomerCode: formData.galiAccountCode,
                officialName: formData.formalName,
                abbreviatedName: formData.abbreviationName,
                branchDepartment: formData.branchDepartment,
                customerNameKatakana: formData.partnerNameKana,
                customerCategory: formData.customerCategory,
                // Required fields for CustomerRegistrationForm but not used in detail
                galiCode: '',
                nameKatakana: '',
                currentAddress: formData.currentAddress,
                headOfficeAddress: formData.headOfficeAddress,
                representatives: formData.representatives,
                isCommonCement: formData.isCommonCement,
              }}
              originalFormData={{
                companyType: '得意先' as const,
                galiCustomerCode: originalFormData.galiAccountCode,
                officialName: originalFormData.formalName,
                abbreviatedName: originalFormData.abbreviationName,
                branchDepartment: originalFormData.branchDepartment,
                customerNameKatakana: originalFormData.partnerNameKana,
                customerCategory: originalFormData.customerCategory,
                // Required fields for CustomerRegistrationForm but not used in detail
                galiCode: '',
                nameKatakana: '',
                currentAddress: originalFormData.currentAddress,
                headOfficeAddress: originalFormData.headOfficeAddress,
                representatives: originalFormData.representatives,
                isCommonCement: originalFormData.isCommonCement,
              }}
              isEditMode={!!originalFormData}
              onUpdateField={handleInfoFieldUpdate}
              getFieldError={getFieldError}
              isFieldValid={isFieldValid}
            />

            {/* Current Address Section */}
            <CurrentAddressSection
              currentAddress={formData.currentAddress}
              originalCurrentAddress={originalFormData.currentAddress}
              onUpdateAddressField={updateCurrentAddress}
              getFieldError={getFieldError}
              isFieldValid={isFieldValid}
            />

            {/* Head Office Address Section */}
            <HeadOfficeAddressSection
              headOfficeAddress={formData.headOfficeAddress}
              originalHeadOfficeAddress={originalFormData.headOfficeAddress}
              onUpdateAddressField={updateHeadOfficeAddress}
              onCopyCurrentToHeadOffice={handleCopyCurrentToHeadOffice}
              getFieldError={getFieldError}
              isFieldValid={isFieldValid}
            />

            {/* Representatives Section */}
            <RepresentativesSection
              representatives={formData.representatives}
              originalRepresentative={originalFormData?.representatives}
              isEditMode={!!originalFormData}
              onAddRepresentative={addRepresentative}
              onUpdateRepresentative={updateRepresentative}
              onRemoveRepresentative={removeRepresentative}
              getSalesPersonOptions={getSalesPersonOptions}
              getFieldError={getFieldError}
              isFieldValid={isFieldValid}
              canAddRepresentative={canAddRepresentative}
              departments={departments}
              departmentsLoading={departmentsLoading}
              staffsLoading={staffsLoading}
              onDepartmentChange={handleDepartmentChange}
            />

            {/* Created & Updated Info Section */}
            <CreatedUpdatedInfoSection
              createdAt={formData.createdAt}
              createdUser={formData.createdStaff ? formData.createdStaff : formData.createdUser}
              updatedAt={formData.updatedAt}
              updatedUser={formData.updatedStaff ? formData.updatedStaff : formData.updatedUser}
            />
          </Box>

          {/* Add bottom padding to prevent content from being hidden behind fixed footer */}
          <Box sx={{ height: '100px' }} />

          {/* Backdrop to prevent interaction when canUpdate is false */}
          <Backdrop
            open={!formData.canUpdate}
            sx={{
              position: 'absolute',
              backgroundColor: 'rgba(0, 0, 0, 0)',
              zIndex: 9999,
            }}
          />
        </Box>
      </Container>

      {/* Add bottom padding to prevent content from being hidden behind fixed footer */}
      <Box sx={{ height: '100px' }} />
      {/* Fixed Action Footer */}
      <FixedFooter
        buttons={[
          {
            label: isLoading ? '削除中...' : '削除',
            onClick: handleDeleteCustomer,
            variant: 'outlined',
            color: 'error',
            startIcon: <DeleteIcon />,
            disabled: isLoading || !formData.canUpdate,
            loading: isLoading,
            sx: {
              color: 'red',
              borderColor: 'red',
              '&:hover': {
                borderColor: 'darkred',
                backgroundColor: 'rgba(255, 0, 0, 0.04)',
              },
            },
          },
          {
            label: 'キャンセル',
            onClick: handleCancel,
            variant: 'outlined',
            startIcon: <CancelIcon />,
            disabled: isLoading,
            sx: {
              marginLeft: 'auto',
            },
          },
          {
            label: isLoading ? '更新中...' : '更新',
            onClick: handleSubmit,
            variant: 'contained',
            startIcon: <EditIcon />,
            disabled: isLoading || !hasChanges || !validation.isValid || !formData.canUpdate,
            loading: isLoading,
          },
        ]}
        isSubmitting={isLoading}
        hasNavigationPanel={false}
      />
    </>
  );
};
